import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { UseDisclosureReturn, useDisclosure } from "@chakra-ui/react";
import {
  Header,
  MetricProto,
  ModelInfo,
  ObservationColumnProto,
  FilterProto,
  ErrorDetailProto,
} from "build-ts/metadata";
import {
  AnalysisStepProto,
  AnalysisPlanProto,
  ObservationEditProto,
  KGObservationProto,
  RecommendationRuleProto,
  RecommendationProto,
  ObservationEditType
} from "build-ts/analysis_plan";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import { v4 as uuidv4 } from "uuid";
import { MetadataTypeEnum } from "build-ts/enums";
import { getNodeData, statefulAnalysisTree } from "services/AnalysisTreeServices";
import { StatefulRequestBuilder } from "containers/stateful/StatefulRequests";
import { ObservationUtils } from "utils/ObservationUtils";
import showToast from "hooks/useCustomToast";
import { getMetricById } from "services/MetricService";
import { createOrUpdatePlan, getPlan, updatePlanHeader, clonePlan } from "services/AnalysisPlanServices";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { GenerateTree_Request, StatefulAnalysisTreeRequest, StatefulResponse } from "build-ts/stateful_api";
import { AnalysisTreeRequestType } from "build-ts/stateful_api";
import { NodeInsights } from "containers/Interfaces";
import { mergeMetricEdgesByType } from "utils/analysisUtils";
import { StepInterface } from "containers/analysis-plan/Interface";
import { buildNodeMapFromPlan } from "utils/analysisPlanUtils";
import * as lodash from "lodash";

interface AnalysisPlanContextType {
  plan: StepInterface | null;
  setPlan: React.Dispatch<React.SetStateAction<StepInterface | null>>;
  selectedNodeId: string | null;
  setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  configurator: UseDisclosureReturn;
  updateNode: (updatedNode: StepInterface) => void;
  addChildToRecommendation: (
    nodeId: string,
    recommendationIndex: number,
    newChild: StepInterface
  ) => void;
  deleteNode: (nodeId: string) => void;
  deleteChildrenOfRecommendation: (
    nodeId: string,
    recommendationIndex: number
  ) => void;
  nodeMap: Map<string, StepInterface>;
  setNodeMap: React.Dispatch<React.SetStateAction<Map<string, StepInterface>>>;
  updateNodeMap: (node: StepInterface) => void;
  metrics: Map<string, MetricProto>;
  setMetrics: React.Dispatch<React.SetStateAction<Map<string, MetricProto>>>;
  models: Map<string, ModelInfo>;
  setModels: React.Dispatch<React.SetStateAction<Map<string, ModelInfo>>>;
  selectedMetricHeader: Header[];
  setSelectedMetricHeader: React.Dispatch<React.SetStateAction<Header[]>>;
  metricList: Header[];
  currentPlanProto: AnalysisPlanProto | undefined;
  executeKGAnalysisPlanHandler: () => void;
  setMetricList: React.Dispatch<React.SetStateAction<Header[]>>;
  getKGObservationFromStepInterface: (nodeId: string) => KGObservationProto;
  saveKGAnalysisPlanHandler: () => Promise<AnalysisPlanProto | null>;
  handleLoadPlan: (planId?: string, planData?: AnalysisPlanProto) => Promise<void>; 
  clonePlan: (planId: string, save?: boolean) => Promise<AnalysisPlanProto | null>;
  updateHeader: (planId: string, name: string, description?: string) => Promise<void>;
  handleNodeSave: (
    nodeId: string,
    nodeData: {
      nodeName: string;
      selectedColumns: ObservationColumnProto[];
      selectedBenchmarks: BenchmarkEntityProto[];
      filters: FilterProto[];
      analysisColumns: ColumnHashProto[];
      benchmarkColumnHashes: ColumnHashProto[];
      recommendationRules: RecommendationRuleProto[];
      selectedMetrics?: MetricProto[];
    },
    latestRecommendations?: RecommendationRuleProto[],
    updatedNode?: StepInterface
  ) => StepInterface | null;
  observationData: NodeInsights | null;
  setObservationData: React.Dispatch<React.SetStateAction<NodeInsights | null>>;
  questions: string[];
  setQuestions: React.Dispatch<React.SetStateAction<string[]>>;
  currentPlanHeader: Header | undefined;
  setCurrentPlanHeader: React.Dispatch<React.SetStateAction<Header | undefined>>;
  handleGetDataForNode: (nodeId: string) => Promise<void>;
  handleAddChildNode: (recommendationIndex: number, currentNode: StepInterface) => Promise<void>;
  openRecommendationNodeId: string | null;
  setOpenRecommendationNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  openRecommendationPopover: (nodeId: string) => void;
  closeRecommendationPopover: () => void;
  getDescendantNodeIds: (rootId: string) => string[];
  isPlanChanged: boolean;
  setIsPlanChanged: React.Dispatch<React.SetStateAction<boolean>>;
  originalNodeMap: Map<string, StepInterface>;
  setOriginalNodeMap: React.Dispatch<React.SetStateAction<Map<string, StepInterface>>>;
  originalQuestions: string[];
  setOriginalQuestions: React.Dispatch<React.SetStateAction<string[]>>;
  isFetchingPlan: boolean;
  errorDetails: ErrorDetailProto[] | null;
  showRecommendedFilters: boolean;
  setShowRecommendedFilters: React.Dispatch<React.SetStateAction<boolean>>;
}

const AnalysisPlanContext = createContext<AnalysisPlanContextType | undefined>(undefined);

export const useAnalysisPlanContext = () => {
  const context = useContext(AnalysisPlanContext);
  if (!context) {
    throw new Error(
      "useKGAnalysisPlanContext must be used within an AnalysisPlanContextProvider"
    );
  }
  return context;
};

// Helper function to update a node.
const updateNode = (
  nodeMap: Map<string, StepInterface>,
  updatedNode: StepInterface
): Map<string, StepInterface> => {
  const newMap = new Map(nodeMap);
  newMap.set(updatedNode.id, updatedNode);
  return newMap;
};

// Helper function to add a child to a recommendation.
const addChildToRecommendation = (
  nodeMap: Map<string, StepInterface>,
  nodeId: string,
  recommendationIndex: number,
  newChild: StepInterface
): Map<string, StepInterface> => {
  const newMap = new Map(nodeMap);
  // Add the new child to the map
  newMap.set(newChild.id, newChild);
  // Get the node we're adding a recommendation child to.
  const node = newMap.get(nodeId);
  if (!node) return newMap;
  // Create or update the recommendation_children_map
  const recommendationChildrenMap =
    node.recommendation_children_map || new Map<number, string[]>();
  const children = recommendationChildrenMap.get(recommendationIndex) || [];
  recommendationChildrenMap.set(recommendationIndex, [
    ...children,
    newChild.id,
  ]);
  // Update the node with the new recommendation_children_map
  const updatedNode = {
    ...node,
    recommendation_children_map: recommendationChildrenMap,
  };
  newMap.set(nodeId, updatedNode);
  return newMap;
};

// Helper function to find the parent node of a given node
const findParentNode = (
  nodeMap: Map<string, StepInterface>,
  childId: string
): { parentNode: StepInterface | null; recommendationIndex: number } => {
  // Use Array.from to convert the Map entries to an array that can be safely iterated
  for (const [, node] of Array.from(nodeMap.entries())) {
    if (node.recommendation_children_map) {
      for (const [index, childIds] of Array.from(
        node.recommendation_children_map.entries()
      )) {
        if (childIds.includes(childId)) {
          return { parentNode: node, recommendationIndex: index };
        }
      }
    }
  }
  return { parentNode: null, recommendationIndex: -1 };
};

// Helper function to get all descendant node IDs (entire subtree)
const getDescendantNodeIds = (
  nodeMap: Map<string, StepInterface>,
  rootId: string
): string[] => {
  const nodeIds: string[] = [];
  const nodeToDelete = nodeMap.get(rootId);

  if (!nodeToDelete || !nodeToDelete.recommendation_children_map) {
    return nodeIds;
  }
  // Collect all direct child IDs from all recommendations
  Array.from(nodeToDelete.recommendation_children_map.entries()).forEach(
    ([, childIds]) => {
      // Add direct children
      nodeIds.push(...childIds);
      // Recursively add descendants of each child
      childIds.forEach((childId) => {
        nodeIds.push(...getDescendantNodeIds(nodeMap, childId));
      });
    }
  );

  return nodeIds;
};

// Helper function to delete a node and its entire subtree
const deleteNode = (
  nodeMap: Map<string, StepInterface>,
  nodeId: string
): Map<string, StepInterface> => {
  // Cannot delete the root node
  if (nodeMap.size <= 1) {
    return nodeMap;
  }
  const newMap = new Map(nodeMap);
  // Find the parent of this node
  const { parentNode, recommendationIndex } = findParentNode(newMap, nodeId);
  if (parentNode) {
    // Remove the node from its parent's recommendation_children_map
    const recommendationChildrenMap =
      parentNode.recommendation_children_map || new Map<number, string[]>();
    const children = recommendationChildrenMap.get(recommendationIndex) || [];
    const updatedChildren = children.filter((id) => id !== nodeId);
    // Update the parent's children list
    recommendationChildrenMap.set(recommendationIndex, updatedChildren);
    // Update the parent node
    const updatedParent = {
      ...parentNode,
      recommendation_children_map: recommendationChildrenMap,
    };
    newMap.set(parentNode.id, updatedParent);
  }
  // Get all descendant node IDs
  const descendantIds = getDescendantNodeIds(newMap, nodeId);
  // Delete the node and all its descendants
  newMap.delete(nodeId);
  descendantIds.forEach((id) => newMap.delete(id));
  return newMap;
};

// Helper function to delete all children of a specific recommendation
const deleteChildrenOfRecommendation = (
  nodeMap: Map<string, StepInterface>,
  nodeId: string,
  recommendationIndex: number
): Map<string, StepInterface> => {
  const newMap = new Map(nodeMap);
  const node = newMap.get(nodeId);
  if (!node) {
    return newMap;
  }
  // Get all child IDs for this recommendation if the map exists
  const childIds =
    node.recommendation_children_map?.get(recommendationIndex) || [];
  // For each child, delete it and its descendants
  const allNodesToDelete: string[] = [];
  childIds.forEach((childId) => {
    // Get all descendants of this child
    const descendantIds = getDescendantNodeIds(newMap, childId);
    allNodesToDelete.push(childId, ...descendantIds);
  });
  // Create updated node with modifications
  const updatedNode = { ...node };
  
  // Remove the recommendation rule itself first
  if (updatedNode.recommendation_rules) {
    updatedNode.recommendation_rules = updatedNode.recommendation_rules.filter(
      (_, index) => index !== recommendationIndex
    );

    // Then create a new recommendation_children_map with proper indexing
    if (updatedNode.recommendation_children_map) {
      const newRecommendationChildrenMap = new Map<number, string[]>();

      // For each entry in the old map
      Array.from(updatedNode.recommendation_children_map.entries()).forEach(
        ([oldIndex, childIds]) => {
          // Skip the deleted recommendation
          if (oldIndex === recommendationIndex) return;

          // Calculate new index based on position
          const newIndex =
            oldIndex > recommendationIndex ? oldIndex - 1 : oldIndex;
          newRecommendationChildrenMap.set(newIndex, childIds);
        }
      );

      updatedNode.recommendation_children_map = newRecommendationChildrenMap;
    }
    
    // Also update the recommendation_edits_map if it exists
    if (updatedNode.recommendation_edits_map) {
      const newRecommendationEditsMap = new Map<number, any[]>();
      
      // For each entry in the old edits map
      Array.from(updatedNode.recommendation_edits_map.entries()).forEach(
        ([oldIndex, edits]) => {
          // Skip the deleted recommendation
          if (oldIndex === recommendationIndex) return;
          
          // Calculate new index based on position
          const newIndex = 
            oldIndex > recommendationIndex ? oldIndex - 1 : oldIndex;
          newRecommendationEditsMap.set(newIndex, edits);
        }
      );
      
      updatedNode.recommendation_edits_map = newRecommendationEditsMap;
    }
  }

  // Update the node in the map
  newMap.set(nodeId, updatedNode);

  // Delete all the nodes to delete
  allNodesToDelete.forEach((id) => newMap.delete(id));

  return newMap;
};

// Helper function to convert an StepInterface to AnalysisStepProto for API submission
export const convertNodeToKGAnalysisStep = (
  node: StepInterface,
  isRootNode: boolean = false,
  nodeMap: Map<string, StepInterface> = new Map()
): AnalysisStepProto => {
  let observationEdits: ObservationEditProto[] = [];

  // If this is a root node, we don't add any observation edits
  if (!isRootNode && node.parent_node_id) {
    // For child nodes, generate observation edits by comparing with parent
    const parentNode = nodeMap.get(node.parent_node_id);
    if (parentNode && node.kg_observation && parentNode.kg_observation) {
      const parentObservation = getKGObservation(parentNode?.id, nodeMap);
      const currentObservation = getKGObservation(node?.id, nodeMap);
      // Generate observation edits by comparing this node's observation with its parent's
      observationEdits = ObservationUtils.getObservationEdits(
        parentObservation, currentObservation);
    }
  }

  // Create the analysis step
  const analysisStep = AnalysisStepProto.create({
    id: node.id.toString(),
    step_label: node.label,
    observation_edits: observationEdits,
    recommendation_rules: node.recommendation_rules || [],
    analysis_columns: [...node?.analysis_columns || [], ...node?.benchmark_column_hashes || []],
  });

  return analysisStep;
};

// New function to build Analysis Plan Proto with KGObservation
export const buildKGAnalysisPlanProto = (
  rootNode: StepInterface,
  nodeMap: Map<string, StepInterface>,
  selectedMetricHeaders: Header[],
  existingPlanHeader?: Header // Add optional parameter for existing plan header
): AnalysisPlanProto => {  
  // Start with the root node
  const rootStep = convertNodeToKGAnalysisStep(rootNode, true /* isRootStep */, nodeMap);
  // Set the child_step_ids for each recommendation rule in the root node
  if (rootNode.recommendation_rules && rootNode.recommendation_children_map) {
    rootNode.recommendation_rules.forEach((rule, index) => {
      // Get child nodes for this recommendation
      const childIds = rootNode.recommendation_children_map?.get(index) || [];
      // Set the child_step_ids property on the rule
      rootStep.recommendation_rules[index].child_step_ids = childIds;
    });
  }
  // Create KGObservationProto for root observation
  const kgObservation = KGObservationProto.create({
    metrics: rootNode.selected_metrics || [], // Use selected_metrics from root node
    benchmarks: [], // Will populate below
    filters: [], // Will populate below
    columns: [],  // Will populate below
  });
  // For root node, always use selected_* fields for the observation
  if (rootNode.selected_benchmarks && rootNode.selected_benchmarks.length > 0) {
    kgObservation.benchmarks = rootNode.selected_benchmarks;
  }
  if (rootNode.selected_columns && rootNode.selected_columns.length > 0) {
    kgObservation.columns = rootNode.selected_columns;
  }
  if (rootNode.selected_filters && rootNode.selected_filters.length > 0) {
    kgObservation.filters = rootNode.selected_filters;
  }
  // All analysis steps to be added to the plan
  const allSteps: AnalysisStepProto[] = [rootStep];
  // Process all nodes in the nodeMap (except the root which we've already added)
  nodeMap.forEach((node, nodeId) => {
    // Skip the root node as we've already added it
    if (nodeId === rootNode.id) {
      return;
    }
    // Convert the node to an AnalysisStepProto
    const step = convertNodeToKGAnalysisStep(node, false /* isRootNode */, nodeMap);
    // Set the child_step_ids for each recommendation rule in this node
    if (node.recommendation_rules && node.recommendation_children_map) {
      node.recommendation_rules.forEach((rule, index) => {
        // Get child nodes for this recommendation
        const childIds = node.recommendation_children_map?.get(index) || [];
        // Set the child_step_ids property on the rule
        step.recommendation_rules[index].child_step_ids = childIds;
      });
    }
    allSteps.push(step);
  });

  // Create the header based on whether we have an existing one
  let header: Header;
  if (existingPlanHeader) {
    // Use the existing header, preserving its name (especially important renamed duplicated plans)
    header = Header.create({
      ...existingPlanHeader,
      id: existingPlanHeader?.id || uuidv4(),
      // Keep the existing header name, don't override with node label
      name: existingPlanHeader.name || "Multi-Metric Analysis",
    });
  } else {
    // Create a minimal header for a new plan
    header = Header.create({
      id: uuidv4(),
      name: nodeMap?.get(rootNode?.id)?.label || "Multi-Metric Analysis",
      description: selectedMetricHeaders.length > 1 ? `Analysis of ${selectedMetricHeaders.length} metrics` : "",
      type: MetadataTypeEnum.ANALYSIS_PLAN
    });
  }

  // Return the full analysis plan with KGObservationProto as root_observation
  const analysisPlan = AnalysisPlanProto.create({
    header: header,
    root_observation: kgObservation,
    root_step_id: rootNode.id,
    analysis_steps: allSteps,
  });
  
  return analysisPlan;
};

// Helper function to get the KGObservation from the given StepInterface
export const getKGObservation = (
  nodeId: string,
  nodeMap: Map<string, StepInterface>
): KGObservationProto => {
  const node = nodeMap.get(nodeId);
  if (!node) {
    // Return an empty observation instead of throwing
    return KGObservationProto.create({
      metrics: [],
      benchmarks: [],
      filters: [],
      columns: [],
    });
  }
  
  // ALWAYS create a fresh KGObservationProto to ensure it's up-to-date
  const kgObservation = KGObservationProto.create({
    metrics: node.selected_metrics || [],
    benchmarks: node.selected_benchmarks || [],
    filters: node.selected_filters || [],
    columns: node.selected_columns || [],
  });
  
  return kgObservation;
};

export const applyRecommendationEdits = (recommendationEdits: RecommendationProto[], currentNode: StepInterface) => {
  let childSelectedColumns = [...(currentNode.selected_columns || [])];
  let childSelectedMetrics = [...(currentNode.selected_metrics || [])];
  let childSelectedBenchmarks = [...(currentNode.selected_benchmarks || [])];
  let childSelectedFilters = [...(currentNode.selected_filters || [])];

  // Apply recommendation edits to modify selections.
  for (const edit of recommendationEdits) {
    // Handle structured edits
    if (edit.edits) {
      for (const observationEdit of edit.edits) {
        switch (observationEdit.edit_type) {
          case ObservationEditType.ADD_COLUMN:
            if (observationEdit.column && !childSelectedColumns.some(col => 
                col.column_id === observationEdit.column?.column_id)) {
              childSelectedColumns.push(observationEdit.column);
            }
            break;
          case ObservationEditType.ADD_METRIC:
            if (observationEdit.metric && !childSelectedMetrics.some(m => 
                m.header?.id === observationEdit.metric?.header?.id)) {
              childSelectedMetrics.push(observationEdit.metric);
            }
            break;
          case ObservationEditType.ADD_BENCHMARK:
            if (observationEdit.benchmark && !childSelectedBenchmarks.some(b => 
                b.header?.id === observationEdit.benchmark?.header?.id)) {
              childSelectedBenchmarks.push(observationEdit.benchmark);
            }
            break;
          case ObservationEditType.REMOVE_COLUMN:
            if (observationEdit.column) {
              childSelectedColumns = childSelectedColumns.filter(col => col.column_id !== observationEdit.column?.column_id);
            }
            break;
          case ObservationEditType.REMOVE_METRIC:
            if (observationEdit.metric) {
              childSelectedMetrics = childSelectedMetrics.filter(m => m.header?.id !== observationEdit.metric?.header?.id);
            }
            break;
          case ObservationEditType.REMOVE_BENCHMARK:
            if (observationEdit.benchmark) {
              childSelectedBenchmarks = childSelectedBenchmarks.filter(b => b.header?.id !== observationEdit.benchmark?.header?.id);
            }
            break;
        }
      }
    }
    
    // Handle select_anyone field (legacy support)
    if (edit?.select_anyone) {
      edit.select_anyone.forEach(columnId => {
        const metric = currentNode?.metrics?.find(m => m.header?.id === columnId);
        if (metric && !childSelectedMetrics.some(m => m.header?.id === columnId)) {
          childSelectedMetrics.push(metric);
        }
        
        const column = currentNode?.non_benchmarks?.find(col => col.column_id === columnId);
        if (column && !childSelectedColumns.some(col => col.column_id === columnId)) {
          childSelectedColumns.push(column);
        }
      });
    }
  }
  return {
    childSelectedColumns,
    childSelectedMetrics,
    childSelectedBenchmarks,
    childSelectedFilters,
  };
};


// Helper function to fetch MKG.
export const fetchMKG = async (metricId: string): Promise<
{ mkg: MetricProto | null }> => {
  // Fetch MKG.
  const response = await getMetricById(metricId, true /* Include edges */);
  if (!response || !response?.header) {
    showToast(`Failed to fetch metric with ID: ${metricId}`, "", "error");
    return { mkg: null };
  }
  return { mkg: response };
};

// Helper function to get the metrics with edges.
export const fetchMetricsWithEdges = async (metricIds: string[]): Promise<MetricProto[]> => {
  const metricPromises = metricIds.map(id => fetchMKG(id));
  const metricResults = await Promise.all(metricPromises);
  return metricResults?.map(result => result?.mkg)
         .filter((data): data is MetricProto => data !== null);
};

export const AnalysisPlanContextProvider: React.FC<{
  children: React.ReactNode;}> = ({ children }) => {
  const [plan, setPlan] = useState<StepInterface | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<Map<string, MetricProto>>(new Map());
  const [models, setModels] = useState<Map<string, ModelInfo>>(new Map());
  const [nodeMap, setNodeMap] = useState<Map<string, StepInterface>>(new Map());
  const [selectedMetricHeader, setSelectedMetricHeader] =
    useState<Header[]>([]);
  const [metricList, setMetricList] = useState<Header[]>([]);
  const [currentPlanHeader, setCurrentPlanHeader] = useState<Header | undefined>(undefined);
  const [questions, setQuestions] = useState<string[]>([]);
  const [observationData, setObservationData] = useState<any | null>(null);
  const [openRecommendationNodeId, setOpenRecommendationNodeId] = useState<string | null>(null);
  // Loading state for plan fetching
  const [isFetchingPlan, setIsFetchingPlan] = useState<boolean>(false);
  // State for tracking plan changes and discard functionality
  const [isPlanChanged, setIsPlanChanged] = useState(false);
  const [originalNodeMap, setOriginalNodeMap] = useState<Map<string, StepInterface>>(new Map());
  const [originalQuestions, setOriginalQuestions] = useState<string[]>([]);
  // Current plan proto
  const [currentPlanProto, setCurrentPlanProto] = useState<AnalysisPlanProto |  undefined>(undefined);
  // Error details of the current node
  const [errorDetails, setErrorDetails] = useState<ErrorDetailProto[] | null>(null);
  const configurator = useDisclosure();

  // State for recommended filters when Get Data is clicked
  const [showRecommendedFilters, setShowRecommendedFilters] = useState<boolean>(false);
  
  // Update a node in the flat map and in the plan if it's the root
  const updateNodeHandler = (updatedNode: StepInterface) => {
    // We'll let KGNodeConfigurator handle the kg_observation refresh
    const newNodeMap = updateNode(nodeMap, updatedNode);
    // If we're updating the root node, track filter changes
    if (plan && plan.id === updatedNode.id) {
      // Extra safety check to ensure metrics are preserved
      if (!updatedNode.metrics && plan.metrics) {
        updatedNode.metrics = plan.metrics;
      }
      setPlan(updatedNode);
    }
    setNodeMap(newNodeMap);
    
    // Auto-save is triggered by useEffect hooks in components watching state changes
  };

  // Generic node save function - can be used for both auto-save and manual save
  const handleNodeSave = useCallback((
    nodeId: string,
    nodeInfo: {
      nodeName: string;
      selectedColumns: ObservationColumnProto[];
      selectedBenchmarks: BenchmarkEntityProto[];
      filters: FilterProto[];
      analysisColumns: ColumnHashProto[];
      benchmarkColumnHashes: ColumnHashProto[];
      recommendationRules: RecommendationRuleProto[];
      selectedMetrics?: MetricProto[];
    },
    latestRecommendations?: RecommendationRuleProto[],
    updatedNode?: StepInterface
  ): StepInterface | null => {
    const currentNode = nodeMap.get(nodeId);
    if (!currentNode) return null;

    // Use selectedMetrics if provided, otherwise fall back to current node selected metrics
    const metrics = nodeInfo.selectedMetrics || currentNode.selected_metrics || [];
    
    // Create KG observation for the current node
    const kgObservation = KGObservationProto.create({
      metrics: [...metrics],
      benchmarks: [...nodeInfo.selectedBenchmarks],
      filters: [...nodeInfo.filters],
      columns: [...nodeInfo.selectedColumns],
    });
    
    // If updatedNode is provided, use it directly
    if (updatedNode) {
      // Just update the recommendations if they were provided
      if (latestRecommendations) {
        updatedNode.recommendation_rules = [...latestRecommendations];
      }
      
      // Make sure we don't lose analysis_columns when updating
      if (nodeInfo.analysisColumns?.length && !updatedNode.analysis_columns?.length) {
        updatedNode.analysis_columns = [...nodeInfo.analysisColumns];
      }

      // Make sure we don't lose benchmark_column_hashes when updating
      if (nodeInfo.benchmarkColumnHashes?.length && !updatedNode.benchmark_column_hashes?.length) {
        updatedNode.benchmark_column_hashes = [...nodeInfo.benchmarkColumnHashes];
      }
      
      updateNodeHandler(updatedNode);
      return updatedNode;
    }
    
    // Update the node with the new data
    const updatedStep: StepInterface = {
      ...currentNode,
      label: nodeInfo.nodeName,
      
      // Preserve available options
      metrics: [...(currentNode.metrics || [])],
      benchmarks: [...(currentNode?.benchmarks || [])],
      non_benchmarks: [...(currentNode?.non_benchmarks || [])],
      temporal_columns: [...(currentNode?.temporal_columns || [])],
      
      // Update selected values
      selected_metrics: [...metrics],
      selected_columns: [...nodeInfo.selectedColumns],
      selected_benchmarks: [...nodeInfo.selectedBenchmarks],
      selected_filters: [...nodeInfo.filters],
      
      // Update analysis columns
      analysis_columns: [...nodeInfo.analysisColumns],

      // Update benchmark column hashes
      benchmark_column_hashes: [...nodeInfo.benchmarkColumnHashes],
      
      // Preserve parent relationship
      parent_node_id: currentNode?.parent_node_id,
      
      // Update recommendations - use latest recommendations if provided
      recommendation_rules: latestRecommendations ? [...latestRecommendations] : [...nodeInfo.recommendationRules],
      
      // Preserve recommendation maps (deep copy)
      recommendation_children_map: new Map(currentNode.recommendation_children_map),
      recommendation_edits_map: new Map(currentNode.recommendation_edits_map),
      
      // Preserve recommendation_edits (deep copy)
      recommendation_edits: [...(currentNode.recommendation_edits || [])],
      
      // Set the KG observation
      kg_observation: kgObservation,
    };
    
    // Update the node
    updateNodeHandler(updatedStep);
    
    return updatedStep;
  }, [nodeMap, updateNodeHandler]);

  // Add a child to a recommendation
  const addChildToRecommendationHandler = (
    nodeId: string,
    recommendationIndex: number,
    newChild: StepInterface
  ) => {
    const newNodeMap = addChildToRecommendation(
      nodeMap,
      nodeId,
      recommendationIndex,
      newChild
    );
    setNodeMap(newNodeMap);
  };

  // Update the node map with a new or updated node
  const updateNodeMapHandler = (node: StepInterface) => {
    setNodeMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(node.id, node);
      return newMap;
    });
  };

  // Delete a node and its entire subtree
  const deleteNodeHandler = (nodeId: string) => {
    // Cannot delete the root node
    if (plan && plan.id === nodeId) {
      return;
    }

    // Close the configurator if the deleted node was selected
    if (selectedNodeId === nodeId) {
      configurator.onClose();
      setSelectedNodeId(null);
    }

    const newNodeMap = deleteNode(nodeMap, nodeId);
    setNodeMap(newNodeMap);
  };

  // Delete all children of a specific recommendation
  const deleteChildrenOfRecommendationHandler = (
    nodeId: string,
    recommendationIndex: number
  ) => {
    const newNodeMap = deleteChildrenOfRecommendation(
      nodeMap,
      nodeId,
      recommendationIndex
    );
    setNodeMap(newNodeMap);
  };

  const executeKGAnalysisPlanHandler = async () => {
    if (!plan || selectedMetricHeader.length === 0) {
      showToast("Missing plan or selected metric", "", "error");
      return;
    }
    // Get the most current version of the root node from nodeMap to ensure we have latest changes
    const currentRootNode = nodeMap.get(plan.id) || plan;
    const proto = buildKGAnalysisPlanProto(currentRootNode, nodeMap, selectedMetricHeader, currentPlanHeader);
    proto.questions = [...questions];
    const transactionId = uuidv4();
    const requestBuilder = new StatefulRequestBuilder(transactionId);
  
    const statefulRequest: StatefulAnalysisTreeRequest[] = [];
    const statefulAnalysisTreeRequest = StatefulAnalysisTreeRequest.create({
      type: AnalysisTreeRequestType.GENERATE_TREE,
      generate_tree: GenerateTree_Request.create({
        analysis_plan: proto,
      }),
    });
    statefulRequest.push(statefulAnalysisTreeRequest);

    const response: StatefulResponse = await statefulAnalysisTree(
      requestBuilder.createStatefulRequest(statefulRequest));
    if (response) {
      window.open(`/analyze?transaction_id=${transactionId}`, "_blank");
      return response;
    }
  };

  const getKGObservationFromStepInterface = (
    nodeId: string
  ): KGObservationProto => {
    return getKGObservation(nodeId, nodeMap);
  };
    
  const saveKGAnalysisPlanHandler = async (): Promise<AnalysisPlanProto | null> => {
    if (!plan || selectedMetricHeader.length === 0) {
      showToast("Missing plan or selected metric", "", "error");
      return null;
    }
  
    // get freshest root
    const currentRootNode = nodeMap.get(plan.id) || plan;
    const proto = buildKGAnalysisPlanProto(
      currentRootNode,
      nodeMap,
      selectedMetricHeader,
      currentPlanHeader
    );
    proto.questions = [...questions];
  
    const response = await createOrUpdatePlan(proto);
    if (!response) {
      showToast("Failed to save plan", "", "error");
      return null;
    }
  
    showToast("Plan saved successfully", "", "success");
  
    // update local plan ID/header
    setCurrentPlanHeader(proto.header);
    return response;
  };
  
  // Handler to load a plan from JSON
  const handleLoadPlan = async (planId?: string, planData?: AnalysisPlanProto) => {
    if (!planId) {
      showToast("No plan header provided.", "", "warning");
      return;
    }
    setIsFetchingPlan(true);
    try{
      // Use provided plan data if available, otherwise fetch from API
      const loadedPlan = planData || await getPlan(planId);
      if (!loadedPlan) {
        return;
      }
      setCurrentPlanProto(loadedPlan);
      // Store the plan ID and header from the action (not the loaded plan)
      // This ensures we track the correct plan ID, especially for duplicated plans
      setCurrentPlanHeader(loadedPlan.header);
      setQuestions([...(loadedPlan?.questions || [])]);
      // Get all metric IDs from the plan's root observation
      const metricIds: Set<string> = new Set();
      // Check for metrics in root_observation
      if (loadedPlan.root_observation?.metrics) {
        loadedPlan.root_observation.metrics.forEach(metric => {
          if (metric.header?.id) {
            metricIds.add(metric.header.id);
          }
        });
      }
      // If no metrics found in root_observation, try looking for a single metric
      if (metricIds.size === 0 && loadedPlan.metric?.header?.id) {
        metricIds.add(loadedPlan.metric.header.id);
      }
      
      // If still no metrics found, show error
      if (metricIds.size === 0) {
        showToast("Plan has no valid metrics", "", "error");
        return;
      }
      const mkgs = await fetchMetricsWithEdges(Array.from(metricIds));
      const metricsMap = new Map<string, MetricProto>(metrics);
      mkgs?.forEach(metric => {
        metricsMap.set(metric.header?.id!, metric);
      });
      setMetrics(metricsMap);
      // Extract headers from mkgs.
      const metricHeaders = mkgs?.map(data => data.header)
        ?.filter((header): header is Header => header !== undefined);
      
      // Update context with selected metric headers
      setSelectedMetricHeader(metricHeaders);
      
      // Build node map from the plan using all fetched metrics
      const { nodeMap: newNodeMap, rootNode } = await buildNodeMapFromPlan(loadedPlan, mkgs);
      
      // Update plan and node map in context
      setPlan(rootNode);
      setNodeMap(newNodeMap);

      // Store original state for change tracking
      setOriginalNodeMap(new Map(Array.from(newNodeMap.entries()).map(([key, value]) => [key, lodash.cloneDeep(value)])));
      setOriginalQuestions([...(loadedPlan?.questions || [])]);
      setIsPlanChanged(false);
    } catch (error) {
      console.error("Error loading plan:", error);
      showToast("Failed to load plan", "", "error");
    } finally {
      setIsFetchingPlan(false);
    }
  };

  // Function to clone a plan by ID
  const clonePlanHandler = async (planId: string, save: boolean = true): Promise<AnalysisPlanProto | null> => {
    // Use the clone service to clone the plan.
    const clonedPlan = await clonePlan(planId, save);
    if (!clonedPlan) {
      showToast("Failed to clone plan", "", "error");
      return null;
    }
    return clonedPlan;
  };

  // Function to get data for a specific node
  const handleGetDataForNode = async (nodeId: string) => {
    const node = nodeMap.get(nodeId);
    if (!node) {
      showToast("Node not found", "", "error");
      return;
    }
    
    // Create KG observation for the node
    const kgObservation = KGObservationProto.create({
      metrics: node?.selected_metrics || [],
      benchmarks: node?.selected_benchmarks || [],
      filters: node?.selected_filters || [],
      columns: node?.selected_columns || [],
      benchmark_columns: node?.benchmark_column_hashes || [],
      analysis_columns: node?.analysis_columns || [],
    });

    const request = AnalysisTreeNodeProto.create({
      id: node.id,
      node_label: node.label,
      node_depth: node.node_depth,
      kg_observation: kgObservation,
      analysis_columns: node?.analysis_columns || [],
    });

    // Call the API to get observation data
    const response = await getNodeData(request);

    // Set error details if they exist
    if(response?.node_results?.error_details){
      setErrorDetails(response.node_results.error_details);
    }
    if (response) {
      // Create NodeInsights structure that DataTableMKG expects
      setObservationData({current_node: response});
      configurator.onClose();
    } else {
      showToast("Failed to fetch observation data", "", "error");
    }
  };

  
  // Add a child node to a recommendation
  const handleAddChildNode = async (recommendationIndex: number, currentNode: StepInterface) => {
    if (!currentNode) return;

    // Get recommendation edits and initialize child selections.
    const recommendationEdits = currentNode.recommendation_edits_map?.get(recommendationIndex) || [];

    let { 
      childSelectedColumns,
      childSelectedMetrics,
      childSelectedBenchmarks,
      childSelectedFilters 
    } = applyRecommendationEdits(recommendationEdits, currentNode);

    childSelectedMetrics = await Promise.all(childSelectedMetrics?.map(async (metric) => {
      if (!metric.edges || metric.edges.length === 0) {
        const updatedMetric = await getMetricById(metric.header?.id!, true /* include edges */);
        return updatedMetric || metric;
      }
      return metric;
    }));
    
    let processedEdges: {
      attributeHeaders: Header[];
      measureHeaders: Header[];
      metricEdgeHeaders: Header[];
      benchmarkEntities: BenchmarkEntityProto[];
      temporalHeaders: Header[];
      metricsHeaders: Header[];
    } = {
      attributeHeaders: [],
      measureHeaders: [],
      metricEdgeHeaders: [],
      benchmarkEntities: [],
      temporalHeaders: [],
      metricsHeaders: [],
    };
    // Calculate available options based on selected metrics
    if (childSelectedMetrics.length > 0) {
      processedEdges = mergeMetricEdgesByType(childSelectedMetrics);
    } else {
      processedEdges = mergeMetricEdgesByType(currentNode?.metrics || []);
    }
    // Get columns from processed edges
    const attributeColumns = ObservationUtils.getObservationColumns(processedEdges?.attributeHeaders) || [];
    const measureColumns = ObservationUtils.getObservationColumns(processedEdges?.measureHeaders) || [];
    const temporalColumns = ObservationUtils.getObservationColumns(processedEdges?.temporalHeaders) || [];

    // Filter parent's non_benchmarks to keep only those whose kg_column_header.id matches processed edges
    const retainedAttributeColumns = currentNode?.non_benchmarks?.filter(column =>
      processedEdges?.attributeHeaders?.some(header => header.id === column.kg_column_header?.id)
    ) || [];
    
    const retainedMeasureColumns = currentNode?.non_benchmarks?.filter(column =>
      processedEdges?.measureHeaders?.some(header => header.id === column.kg_column_header?.id)
    ) || [];
    
    const retainedTemporalColumns = currentNode?.non_benchmarks?.filter(column =>
      processedEdges?.temporalHeaders?.some(header => header.id === column.kg_column_header?.id)
    ) || [];

    // Add new columns from processed edges that don't exist in retained columns
    const newAttributeColumns = attributeColumns.filter(newCol =>
      !retainedAttributeColumns.some(retained => retained.kg_column_header?.id === newCol.kg_column_header?.id)
    );
    
    const newMeasureColumns = measureColumns.filter(newCol =>
      !retainedMeasureColumns.some(retained => retained.kg_column_header?.id === newCol.kg_column_header?.id)
    );
    
    const newTemporalColumns = temporalColumns.filter(newCol =>
      !retainedTemporalColumns.some(retained => retained.kg_column_header?.id === newCol.kg_column_header?.id)
    );

    // Combine retained and new columns
    const availableAttributeColumns = [...retainedAttributeColumns, ...newAttributeColumns];
    const availableMeasureColumns = [...retainedMeasureColumns, ...newMeasureColumns];
    const availableTemporalColumns = [...retainedTemporalColumns, ...newTemporalColumns];   
    
    // Include both processed benchmarks and growth benchmarks.
    const availableBenchmarks = [...(processedEdges?.benchmarkEntities || [])];
    // Push growth benchmarks to available benchmarks.
    currentNode?.benchmarks?.forEach(benchmark => {
      if (benchmark.growth_benchmark) {
        availableBenchmarks.push(benchmark);
      }
    });

    // Filter selections to only include available options.
    const allAvailableColumns = [
      ...availableAttributeColumns,
      ...availableMeasureColumns,
      ...availableTemporalColumns
    ];
    
    // Remove columns that are not available but selected in parent node.
    childSelectedColumns = childSelectedColumns.filter(col =>
      allAvailableColumns.some(c => c.column_id === col.column_id)
    );
    
    // Remove benchmarks that are not available but selected in parent node.
    childSelectedBenchmarks = childSelectedBenchmarks.filter(benchmark => {
      if (benchmark.growth_benchmark) return true;
      return availableBenchmarks.some(b => b.header?.id === benchmark?.header?.id);
    });

    // Generate all column hash combinations (including basic ones for recommendations)
    const allParentColumnHashes = currentNode?.analysis_columns || [];
    const allParentBenchmarkColumnHashes = currentNode?.benchmark_column_hashes || [];
    
    // Generate valid analysis columns for child node based on selected metrics and benchmarks
    const validAnalysisColumns: ColumnHashProto[] = [];
    const childBenchmarkIds = new Set<string>();
    
    // Collect benchmark IDs from selected benchmarks
    childSelectedBenchmarks?.forEach(benchmark => {
      if (benchmark.header?.id) {
        childBenchmarkIds.add(benchmark.header.id);
      }
    });
    
    // Get all column IDs for child node
    const childColumnIds = new Set<string>();
    childSelectedColumns.forEach(col => {
      if (col.column_id) childColumnIds.add(col.column_id);
    });
    childSelectedMetrics?.forEach(metric => {
      if (metric.header?.id) childColumnIds.add(metric.header.id);
    });
    
    // Only include analysis columns (those with analysis_column_type) that match child selections
    allParentColumnHashes?.forEach(columnHash => {
      if (columnHash?.analysis_column_type && 
          columnHash?.actuals_id && 
          childColumnIds?.has(columnHash.actuals_id) &&
          (!columnHash?.benchmark_id || childBenchmarkIds?.has(columnHash.benchmark_id))) {
        validAnalysisColumns.push(columnHash);
      }
    });

    // Create and add child node
    const newChild: StepInterface = {
      id: uuidv4(),
      label: `New Child Node ${nodeMap.size + 1}`,
      type: "transform",
      parent_node_id: currentNode.id,
      
      // Available options for child node
      metrics: [...(currentNode?.metrics || [])],
      benchmarks: [...availableBenchmarks],
      non_benchmarks: [...allAvailableColumns],
      temporal_columns: [...availableTemporalColumns],
      
      // Selected values after applying edits
      selected_metrics: [...childSelectedMetrics],
      selected_columns: [...childSelectedColumns],
      selected_benchmarks: [...childSelectedBenchmarks],
      selected_filters: [...childSelectedFilters],
      analysis_columns: validAnalysisColumns,
      benchmark_column_hashes: [...allParentBenchmarkColumnHashes],
      
      // Initialize empty recommendation data
      recommendation_rules: [],
      recommendation_children_map: new Map(),
      recommendation_edits_map: new Map(),
      recommendation_edits: [...(currentNode.recommendation_edits || []), ...recommendationEdits],
      kg_observation: null,
    };

    // Add child to the recommendation
    addChildToRecommendationHandler(currentNode?.id, recommendationIndex, newChild);
  };

  const getDescendantNodeIdsHandler = (rootId: string): string[] => {
    return getDescendantNodeIds(nodeMap, rootId);
  };
  
  // Recommendation popover state handlers
  const openRecommendationPopover = (nodeId: string) => {
    setOpenRecommendationNodeId(nodeId);
  };

  const closeRecommendationPopover = () => {
    setOpenRecommendationNodeId(null);
  };

  // When the tree changes, make sure it's in the nodeMap
  useEffect(() => {
    if (plan) {
      updateNodeMapHandler(plan);
    }
  }, [plan]);

  // Function to update plan header
  const updateHeaderHandler = async (planId: string, name: string, description?: string) => {
    const response = await updatePlanHeader(planId, name, description);
    if (response) {
      // Update the current plan header in context
      setCurrentPlanHeader(prevHeader => ({
        ...prevHeader,
        name,
        description: description || prevHeader?.description
      }));
      showToast("Plan header updated successfully", "", "success");
    }
  };

  return (
    <AnalysisPlanContext.Provider
      value={{
        plan,
        setPlan,
        selectedNodeId,
        setSelectedNodeId,
        configurator,
        updateNode: updateNodeHandler,
        addChildToRecommendation: addChildToRecommendationHandler,
        deleteNode: deleteNodeHandler,
        deleteChildrenOfRecommendation: deleteChildrenOfRecommendationHandler,
        nodeMap,
        setNodeMap,
        updateNodeMap: updateNodeMapHandler,
        metrics,
        setMetrics,
        models,
        setModels,
        selectedMetricHeader,
        setSelectedMetricHeader,
        metricList,
        currentPlanProto,
        executeKGAnalysisPlanHandler,
        setMetricList,
        getKGObservationFromStepInterface,
        saveKGAnalysisPlanHandler,
        clonePlan: clonePlanHandler,
        handleLoadPlan,
        updateHeader: updateHeaderHandler,
        handleNodeSave,
        observationData,
        setObservationData,
        questions,
        setQuestions,
        currentPlanHeader,
        setCurrentPlanHeader,
        handleGetDataForNode,
        handleAddChildNode,
        openRecommendationNodeId,
        setOpenRecommendationNodeId,
        openRecommendationPopover,
        closeRecommendationPopover,
        getDescendantNodeIds: getDescendantNodeIdsHandler,
        isPlanChanged,
        setIsPlanChanged,
        originalNodeMap,
        setOriginalNodeMap,
        originalQuestions,
        setOriginalQuestions,
        isFetchingPlan,
        errorDetails,
        showRecommendedFilters,
        setShowRecommendedFilters,
      }}
    >
      {children}
    </AnalysisPlanContext.Provider>
  );
};
